// src/hooks/useVideoElement.ts
// Custom hook for managing video elements with state synchronization
// This encapsulates video element ownership and state management

import { useCallback, useEffect, useRef, useState } from 'react'

interface VideoElementState {
  isPlaying: boolean
  position: number
  duration: number
  isReady: boolean
  error: string | null
}

interface VideoElementActions {
  play: () => Promise<void>
  pause: () => void
  seek: (time: number) => void
  setVolume: (volume: number) => void
  setMuted: (muted: boolean) => void
}

interface UseVideoElementOptions {
  onEnded?: () => void
  onTimeUpdate?: (currentTime: number) => void
  onReady?: () => void
  onError?: (error: Event) => void
  muted?: boolean
  volume?: number
  loop?: boolean
  reloadToken?: number | string
}

export function useVideoElement(src: string | null, options: UseVideoElementOptions = {}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [managedElement, setManagedElement] = useState<HTMLVideoElement | null>(null)
  const [state, setState] = useState<VideoElementState>({
    isPlaying: false,
    position: 0,
    duration: 0,
    isReady: false,
    error: null,
  })

  // Track last processed src to avoid duplicate processing
  const lastProcessedSrcRef = useRef<string | null>(null)
  const lastProcessedTokenRef = useRef<string | number>('')

  // Store callbacks in refs to avoid re-running effect when callbacks change
  const callbacksRef = useRef(options)
  callbacksRef.current = options

  // Store isReady in a ref for the early-return check (don't want it as a dependency)
  const isReadyRef = useRef(state.isReady)
  isReadyRef.current = state.isReady

  // Throttled position updates to reduce state churn
  const lastUpdateTimeRef = useRef(0)
  const updatePosition = useCallback((currentTime: number) => {
    // Throttle position updates to max 10 times per second (every 100ms)
    const now = Date.now()
    if (now - lastUpdateTimeRef.current < 100) return
    lastUpdateTimeRef.current = now

    setState((prev) => (prev.position !== currentTime ? { ...prev, position: currentTime } : prev))
    callbacksRef.current.onTimeUpdate?.(currentTime)
  }, [])

  // Store updatePosition in a ref so the effect doesn't need it as a dependency
  const updatePositionRef = useRef(updatePosition)
  updatePositionRef.current = updatePosition

  useEffect(() => {
    videoRef.current = managedElement || null
  }, [managedElement])

  // Setup video element when src or managed element changes
  useEffect(() => {
    const video = managedElement
    if (!video || !src) return

    const nextReloadToken = callbacksRef.current.reloadToken ?? ''

    // Check if the video element already has this source loaded
    // This handles the case where useVideoTexture has already set the source
    const absoluteSrc = typeof window !== 'undefined' ? new URL(src, window.location.href).href : src
    const isSourceAlreadyLoaded = video.src === absoluteSrc

    // Check if video has usable data (readyState >= 2 means HAVE_CURRENT_DATA)
    const hasVideoData = video.readyState >= 2 && video.videoWidth > 0

    // Determine if we've already processed this exact src + token combination
    const alreadyProcessed = lastProcessedSrcRef.current === src && lastProcessedTokenRef.current === nextReloadToken

    // Skip ONLY if we've already processed this combination AND the video has data AND is ready
    // This prevents duplicate loads when the video element is already set up correctly
    if (alreadyProcessed && isSourceAlreadyLoaded && hasVideoData && isReadyRef.current) {
      console.debug(
        ['useVideoElement: Skipping - already processed and loaded', { src, readyState: video.readyState }],
        'video'
      )
      return
    }

    // Track that we're processing this src + token
    lastProcessedSrcRef.current = src
    lastProcessedTokenRef.current = nextReloadToken

    // Determine if we need to actually load the video or just adopt the existing state
    const needsLoad = !isSourceAlreadyLoaded
    const isAdopting = isSourceAlreadyLoaded

    console.debug(
      [
        'useVideoElement: Processing video',
        {
          src,
          needsLoad,
          isAdopting,
          isSourceAlreadyLoaded,
          hasVideoData,
          readyState: video.readyState,
        },
      ],
      'video'
    )

    // Update our tracking dataset
    video.dataset.managedSrc = src
    video.dataset.managedReloadToken = String(nextReloadToken)

    if (needsLoad) {
      // Only set src and call load() if the source isn't already set
      setState((prev) => ({
        ...prev,
        isReady: false,
        duration: 0,
        position: 0,
      }))

      video.preload = 'auto'
      video.src = src
      video.load()

      try {
        video.currentTime = 0
      } catch (error) {
        console.warn('Unable to reset video currentTime during reload', error)
      }
    } else if (isAdopting) {
      // Adopting an existing loaded video - sync our state with the video's current state
      console.debug(
        [
          'useVideoElement: Adopting existing video element',
          { duration: video.duration, readyState: video.readyState, hasVideoData },
        ],
        'video'
      )

      if (hasVideoData && video.duration > 0) {
        // Video is already ready - immediately update state
        setState((prev) => ({
          ...prev,
          duration: video.duration,
          position: video.currentTime,
          isReady: true,
        }))
        callbacksRef.current.onReady?.()
      } else {
        // Video source is set but not fully loaded yet - reset state and wait for events
        setState((prev) => ({
          ...prev,
          isReady: false,
          duration: 0,
          position: 0,
        }))
      }
    }

    // Helper to check if video is ready and update state
    const checkAndSetReady = () => {
      if (video.readyState >= 1 && video.duration > 0) {
        console.log(['Video metadata ready', { duration: video.duration, readyState: video.readyState, src }], 'video')
        setState((prev) => {
          if (prev.isReady) return prev // Already ready, no update needed
          return { ...prev, duration: video.duration, isReady: true }
        })
        callbacksRef.current.onReady?.()
        return true
      }
      return false
    }

    // Check if metadata is already loaded (handles cached videos and adoption)
    // Use setTimeout to let the browser update readyState after load()
    setTimeout(() => {
      checkAndSetReady()
    }, 0)

    const handleLoadedMetadata = () => {
      console.log(['Video metadata loaded event', { duration: video.duration, src }], 'video')
      checkAndSetReady()
    }

    // Also listen for canplay as a fallback - some browsers fire this more reliably
    const handleCanPlay = () => {
      console.debug(['Video canplay event', { duration: video.duration, readyState: video.readyState }], 'video')
      checkAndSetReady()
    }

    // Throttled time update handler
    let rafId: number | null = null
    const handleTimeUpdate = () => {
      // Use requestAnimationFrame to throttle updates to display refresh rate
      if (rafId !== null) return
      rafId = requestAnimationFrame(() => {
        updatePositionRef.current(video.currentTime)
        rafId = null
      })
    }

    const handleEnded = () => callbacksRef.current.onEnded?.()
    const handleError = (e: Event) => {
      console.log(['Video error', { src, error: e }], 'video')
      setState((prev) => ({ ...prev, error: 'Video failed to load' }))
      callbacksRef.current.onError?.(e)
    }

    const handlePlay = () => setState((prev) => ({ ...prev, isPlaying: true }))
    const handlePause = () => setState((prev) => ({ ...prev, isPlaying: false }))

    video.addEventListener('loadedmetadata', handleLoadedMetadata)
    video.addEventListener('canplay', handleCanPlay)
    video.addEventListener('timeupdate', handleTimeUpdate)
    video.addEventListener('ended', handleEnded)
    video.addEventListener('error', handleError)
    video.addEventListener('play', handlePlay)
    video.addEventListener('pause', handlePause)

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId)
      }
      video.removeEventListener('loadedmetadata', handleLoadedMetadata)
      video.removeEventListener('canplay', handleCanPlay)
      video.removeEventListener('timeupdate', handleTimeUpdate)
      video.removeEventListener('ended', handleEnded)
      video.removeEventListener('error', handleError)
      video.removeEventListener('play', handlePlay)
      video.removeEventListener('pause', handlePause)
    }
  }, [managedElement, src])

  // Sync options to video element
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    video.muted = options.muted ?? false
    video.volume = options.volume ?? 1.0
    video.loop = options.loop ?? false
  }, [options.muted, options.volume, options.loop])

  const attachVideoElement = useCallback((element: HTMLVideoElement | null) => {
    setManagedElement((prev) => (prev === element ? prev : element))
    videoRef.current = element
  }, [])

  const play = useCallback(async () => {
    const video = videoRef.current
    if (!video) return
    try {
      await video.play()
    } catch (error) {
      if (error instanceof DOMException) {
        if (error.name === 'AbortError') {
          console.debug('Video play interrupted by a new load request.', 'useVideoElement')
          return
        }
        if (error.name === 'NotAllowedError') {
          console.warn('Autoplay prevented; awaiting user interaction.', 'useVideoElement')
          return
        }
      }
      console.error('Failed to play video:', error)
    }
  }, [])

  const pause = useCallback(() => {
    const video = videoRef.current
    if (!video) return
    video.pause()
  }, [])

  const seek = useCallback(
    (time: number) => {
      const video = videoRef.current
      if (!video) return
      video.currentTime = time
      updatePosition(time)
    },
    [updatePosition]
  )

  const setVolumeAction = useCallback((volume: number) => {
    const video = videoRef.current
    if (!video) return
    video.volume = volume
  }, [])

  const setMutedAction = useCallback((muted: boolean) => {
    const video = videoRef.current
    if (!video) return
    video.muted = muted
  }, [])

  return {
    videoRef,
    attachVideoElement,
    ...state,
    play,
    pause,
    seek,
    setVolume: setVolumeAction,
    setMuted: setMutedAction,
  }
}
